import { useState, useEffect } from 'react';
import { setupRecaptcha, sendOTP, verifyOTP } from '../services/authService';
import { saveRegistration, checkDuplicateRegistration, getRegistrationCount } from '../services/registrationService';
import { sanitizeFormData, buildPhoneNumber } from '../utils/inputValidation';
import ErrorMessage from './ErrorMessage';
import PhoneField from './PhoneField';
import { DISABILITY_OPTIONS } from '../config/disabilityOptions';
import { isRegistrationOpen } from '../config/events';

/**
 * @param {Object} event - full event object from events.js to register for
 * @param {Function} [onRegistered] - called with (formData, userId) after a successful save,
 *   so a parent can offer registering for other concurrently active events
 */
const RegistrationForm = ({ event, onRegistered }) => {
  const [formData, setFormData] = useState({
    name: '',
    phoneNumber: '',
    email: '',
    gender: '',
    disability: '',
    disabilityOther: '',
    disabilityIsOther: false,
    isAbove18: false
  });
  // Phone is entered as two parts; formData.phoneNumber is built from them on submit
  const [countryCode, setCountryCode] = useState('91');
  const [localNumber, setLocalNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('form');
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const [isFull, setIsFull] = useState(false);
  const [checkingCapacity, setCheckingCapacity] = useState(true);
  const [timerStarted, setTimerStarted] = useState(false);
  // Checked on load and again on submit (closesAt may pass while the page is open).
  // Not re-checked during OTP, so someone who started in time can finish.
  const [isOpen, setIsOpen] = useState(() => isRegistrationOpen(event));

  useEffect(() => {
    setupRecaptcha('recaptcha-container');
  }, []);

  useEffect(() => {
    const checkCapacity = async () => {
      const count = await getRegistrationCount(event.id);
      setIsFull(count >= event.registration.cap);
      setCheckingCapacity(false);
    };
    checkCapacity();
  }, [event]);

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  useEffect(() => {
    if (resendTimer === 0 && step === 'otp' && timerStarted) {
      document.getElementById('resend-status').textContent = 'Resend OTP is now available.';
    }
  }, [resendTimer, step, timerStarted]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setError('');
  };

  const handleCountryCodeChange = (value) => {
    setCountryCode(value);
    setError('');
  };

  const handleLocalNumberChange = (value) => {
    setLocalNumber(value);
    setError('');
  };

  const handleDisabilityChange = (e) => {
    const value = e.target.value;
    const isOther = value === 'other';
    setFormData(prev => ({
      ...prev,
      disability: isOther ? '' : value,
      disabilityIsOther: isOther,
      disabilityOther: isOther ? prev.disabilityOther : ''
    }));
    setError('');
  };

  const validateForm = (phone) => {
    if (!formData.name.trim()) return 'Please enter your name';
    if (phone.error) return phone.error;
    if (!formData.email.trim()) return 'Please enter your email address';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) return 'Please enter a valid email address';
    if (!formData.gender) return 'Please select your gender';
    if (!formData.disability && !formData.disabilityIsOther) return 'Please select your disability or condition';
    if (formData.disabilityIsOther && !formData.disabilityOther.trim()) return 'Please describe your disability or condition';
    if (!formData.isAbove18) return 'You must confirm you are 18 or above';
    return null;
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    const phone = buildPhoneNumber(countryCode, localNumber);
    const validationError = validateForm(phone);
    if (validationError) { setError(validationError); return; }
    if (!isRegistrationOpen(event)) { setIsOpen(false); return; }

    setLoading(true);
    setError('');

    const cleanData = sanitizeFormData({ ...formData, phoneNumber: phone.phoneNumber });
    const isDuplicate = await checkDuplicateRegistration(cleanData.phoneNumber, event.id);
    if (isDuplicate) {
      setError('You have already registered for this event.');
      setLoading(false);
      return;
    }

    const result = await sendOTP(cleanData.phoneNumber);
    if (result.success) {
      setConfirmationResult(result.confirmationResult);
      setFormData(cleanData);
      setStep('otp');
      setResendTimer(30);
      setTimerStarted(true);
      document.getElementById('sr-announcement').textContent = 'OTP sent successfully. Please check your phone and enter the code.';
      document.getElementById('resend-status').textContent = 'Resend OTP will be available in 30 seconds.';
    } else {
      setError(result.error || 'Failed to send OTP. Please try again.');
    }
    setLoading(false);
  };

  const handleResendOTP = async () => {
    if (resendTimer > 0) return;
    setLoading(true);
    setError('');
    setOtp('');

    const result = await sendOTP(formData.phoneNumber);
    if (result.success) {
      setConfirmationResult(result.confirmationResult);
      setResendTimer(30);
      setTimerStarted(true);
      document.getElementById('sr-announcement').textContent = 'New OTP sent successfully.';
      document.getElementById('resend-status').textContent = 'Resend OTP will be available in 30 seconds.';
    } else {
      setError(result.error || 'Failed to resend OTP. Please try again.');
    }
    setLoading(false);
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (!otp || otp.length < 6) { setError('Please enter a valid 6-digit OTP'); return; }

    setLoading(true);
    setError('');

    const result = await verifyOTP(confirmationResult, otp);
    if (result.success) {
      const saveResult = await saveRegistration(formData, result.user.uid, event);
      if (saveResult.success) {
        setStep('success');
        document.getElementById('sr-announcement').textContent = 'Registration successful! Thank you for joining.';
        onRegistered?.(formData, result.user.uid);
      } else {
        setError(saveResult.error || 'Registration failed. Please try again.');
      }
    } else {
      setError(result.error || 'Invalid OTP. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div className="registration-container">
      <div id="sr-announcement" className="sr-only" role="status" aria-live="polite" aria-atomic="true"></div>
      <div id="resend-status" className="sr-only" role="status" aria-live="polite" aria-atomic="true"></div>
      <div id="recaptcha-container"></div>

      {checkingCapacity ? (
        <div className="checking-capacity"><p>Loading registration form...</p></div>

      ) : !isOpen ? (
        <div className="registration-closed">
          <div className="closed-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 6v6l4 2"/>
            </svg>
          </div>
          <h2>Registration Closed</h2>
          <p className="closed-message">Registration for this event is now closed.</p>
          <p className="stay-tuned">Stay tuned for future Buddy Up community events and opportunities to connect.</p>
          <div className="closed-cta"><p>Download our app to stay updated on upcoming events</p></div>
        </div>

      ) : isFull ? (
        <div className="registration-closed">
          <div className="closed-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 6v6l4 2"/>
            </svg>
          </div>
          <h2>Registration Full</h2>
          <p className="closed-message">We've reached capacity for this event. Thank you for your interest!</p>
          <p className="stay-tuned">Stay tuned for future Buddy Up community events and opportunities to connect.</p>
          <div className="closed-cta"><p>Download our app to stay updated on upcoming events</p></div>
        </div>

      ) : (
        <>
          {step === 'form' && (
            <form onSubmit={handleSendOTP} className="registration-form">
              <div className="form-title">
                <h1>{event.name}</h1>
                <span className="form-type-badge">Registration</span>
                <hr className="title-divider" />
              </div>

              <div className="form-group">
                <label htmlFor="name">Full Name <span aria-label="required">*</span></label>
                <input
                  type="text" id="name" name="name"
                  value={formData.name} onChange={handleInputChange}
                  required aria-required="true" autoComplete="name"
                />
              </div>

              <div className="otp-notice">
                <p><strong>Important:</strong> An OTP (One-Time Password) will be sent to your mobile number for verification. Please keep your phone ready.</p>
              </div>

              <PhoneField
                countryCode={countryCode}
                number={localNumber}
                onCountryCodeChange={handleCountryCodeChange}
                onNumberChange={handleLocalNumberChange}
              />

              <div className="form-group">
                <label htmlFor="email">Email Address <span aria-label="required">*</span></label>
                <input
                  type="email" id="email" name="email"
                  value={formData.email} onChange={handleInputChange}
                  required aria-required="true" autoComplete="email"
                  placeholder="you@example.com"
                />
              </div>

              <div className="form-group">
                <fieldset>
                  <legend id="gender-legend">Gender <span aria-label="required">*</span></legend>
                  <div className="radio-group" role="radiogroup" aria-labelledby="gender-legend">
                    {['male', 'female', 'other'].map(g => (
                      <label key={g}>
                        <input
                          type="radio" name="gender" value={g}
                          checked={formData.gender === g} onChange={handleInputChange} required
                        />
                        <span>{g.charAt(0).toUpperCase() + g.slice(1)}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
              </div>

              <div className="form-group">
                <label htmlFor="disability-select">
                  Disability / Condition <span aria-label="required">*</span>
                </label>
                <select
                  id="disability-select"
                  name="disability"
                  className="disability-native-select"
                  value={formData.disabilityIsOther ? 'other' : formData.disability}
                  onChange={handleDisabilityChange}
                  required
                  aria-required="true"
                >
                  <option value="" disabled>Select your disability or condition</option>
                  {DISABILITY_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                {formData.disabilityIsOther && (
                  <input
                    type="text"
                    name="disabilityOther"
                    value={formData.disabilityOther}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, disabilityOther: e.target.value, disability: e.target.value }));
                      setError('');
                    }}
                    placeholder="Please describe your disability or condition"
                    aria-label="Describe your disability or condition"
                    style={{ marginTop: '10px' }}
                  />
                )}
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox" name="isAbove18"
                    checked={formData.isAbove18} onChange={handleInputChange}
                    required aria-required="true"
                  />
                  <span>I confirm that I am 18 years or above</span>
                </label>
              </div>

              <ErrorMessage message={error} />

              <button type="submit" disabled={loading} className="submit-button">
                {loading ? 'Sending OTP...' : 'Send OTP'}
              </button>
            </form>
          )}

          {step === 'otp' && (
            <form onSubmit={handleVerifyOTP} className="otp-form">
              <h2>Enter OTP</h2>
              <p id="otp-instruction">We've sent a 6-digit code to {formData.phoneNumber}</p>

              <div className="form-group">
                <label htmlFor="otp">Enter OTP <span aria-label="required">*</span></label>
                <input
                  type="text" id="otp" name="otp"
                  value={otp} onChange={(e) => setOtp(e.target.value)}
                  maxLength="6" pattern="[0-9]{6}"
                  required aria-required="true"
                  aria-describedby="otp-instruction"
                  autoComplete="one-time-code" inputMode="numeric"
                />
              </div>

              <ErrorMessage message={error} />

              <button type="submit" disabled={loading} className="submit-button">
                {loading ? 'Verifying...' : 'Verify OTP'}
              </button>
              <button
                type="button" onClick={handleResendOTP}
                disabled={resendTimer > 0 || loading}
                className="resend-button"
              >
                {resendTimer > 0 ? `Resend OTP in ${resendTimer}s` : 'Resend OTP'}
              </button>
            </form>
          )}

          {step === 'success' && (
            <div className="success-message" role="alert">
              <h2>Registration Successful!</h2>
              <p>Thank you for registering with Buddy Up.</p>
              <p>You'll receive the event link closer to the event date.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default RegistrationForm;